import { FormikProps } from 'formik';
import { getAnnualEnrollmentTierCalc, getVolumeForSpecificPlans, postDioApproverApproverGhlForm, postGhlForm, postMidYearCoverageEnrollment, postNewHireCoverageEnrollment } from '../../../../../../global/ducks/mlps-api';
import { getFormattedDateYmd } from '../../../../../../global/helpers/date-helpers';
import { YesNoIndicators } from '../../../../../../global/models/api-model';
import { SelfText } from '../../../../../annual-enrollment/models/annual-enrollment-models';
import { BillingAccountOptionValues } from '../../../new-participant-enrollment/models/form-options';
import { CalculatedTiers } from '../../models/new-hire-enrollment-models';
import { BenefitEnrollmentStep } from '../../models/pension-enrollment-models';
import { WhoWillEnrollCriterias, WhoWillEnrollOptions } from '../../models/who-will-enroll-models';
import { setCurrentNewHireBenefitEnrollmentStep, setNewHireTier, setVolumeAndRate } from '../../slice/new-hire-enrollment-slices';
import { CoverageFormModel, CoveragePerson, PlanTypes, RelationshipCode, CoveragePlans, BillingPrefernce, NewHireCoverageState, declineCoverageReasonOther, FormInfo, CoverageValidationSchemaModel, DioApproverInitialValues, MemberSelectionRequiredValidationSchemaModel, DeclinedValue } from '../models/coverage-models';
import { ArcRoutes } from '../../../../constants/routes';

export function getDependentCountByRelationship(selectedPeople: string[], eligibleDependents: CoveragePerson[], relationshipCodes: RelationshipCode[]) {
	return selectedPeople.filter(person => eligibleDependents?.some(dep => relationshipCodes.includes(dep?.relationship) && dep.guid === person))?.length || 0;
}

export function calculateTiers(plan: CoveragePlans, selectedPeople: string[], dispatch: any, eligible_dependents: CoveragePerson[], planType: PlanTypes, isDioApprover?: boolean) {

	if (!plan?.plans[0]?.rates_by_tier) {
		return;
	}
	const calculateDioApproverInitialValues = !!isDioApprover && !!plan?.selected?.tier;

	const tierCount = Object.keys(plan?.plans[0]?.rates_by_tier)?.length;
	const spouseCount = getDependentCountByRelationship(selectedPeople, eligible_dependents, [RelationshipCode.Spouse, RelationshipCode.DomesticPartner]);
	const childCount = getDependentCountByRelationship(selectedPeople, eligible_dependents, [RelationshipCode.Child, RelationshipCode.StepChild, RelationshipCode.Disabled]);

	if (calculateDioApproverInitialValues) {
		dispatch(setNewHireTier(planType, { display_tier: plan?.selected?.tier, selected_tier: plan?.selected?.tier }));
		return;
	}

	dispatch(getAnnualEnrollmentTierCalc(plan?.group_id, tierCount, spouseCount, childCount, (response) => {
		dispatch(setNewHireTier(planType, response));
	}));
}

export function onCoverageChange(
	e,
	planType: PlanTypes,
	formProps: FormikProps<CoverageFormModel>,
	plan: CoveragePlans,
	dispatch: any,
	dependents: CoveragePerson[],
	setRemoveSelfFlag?: any,
) {
	const { value } = e?.target;

	if (planType === PlanTypes.M && formProps?.values?.medicalDependents?.length === 0) {
		formProps.setFieldValue('medicalDependents', [SelfText]);
		calculateTiers(plan, [SelfText], dispatch, dependents, planType);

	}

	if (planType === PlanTypes.D && formProps?.values?.dentalDependents?.length === 0) {
		formProps.setFieldValue('dentalDependents', [SelfText]);
		calculateTiers(plan, [SelfText], dispatch, dependents, planType);

	}

	if ((!value || value === DeclinedValue) && setRemoveSelfFlag) {
		setRemoveSelfFlag(planType);
	} else {
		formProps.setFieldValue(planType, value);
	}

}


export function getMatchedPlan(code: string, plans: CoveragePlans) {
	return plans?.plans?.find(p => p?.code === code);
}

export function transformFormData(plans: CoveragePlans, planType: PlanTypes, billingAccountKey: keyof CoverageFormModel, values: CoverageFormModel, tier?: string, volume?: string, midYear_effective_date?: string) {
	const plan = getMatchedPlan(values[planType], plans);

	if (values[planType] === DeclinedValue) {
		return {
			declined: YesNoIndicators.Yes,
		};
	}

	return plan ?
		{
			codes: [plan?.code],
			account_number:
				values[billingAccountKey] === BillingAccountOptionValues.INSTITUTION ?
					plans?.account_number :
					plans?.diocese_account_number,
			effective_date: getFormattedDateYmd(midYear_effective_date ?? plans?.effective_date, true),
			group_id: plans?.group_id,
			volume,
			tier,
			declined: null,
		}
		: null;
}

export function transformFormDataToPushToMember(
	plans: CoveragePlans,
	billingAccountKey: keyof CoverageFormModel,
	values: CoverageFormModel,
	form_info?: FormInfo,
) {
	return {
		account_number: values[billingAccountKey] === BillingAccountOptionValues.INSTITUTION ?
			plans?.account_number :
			plans?.diocese_account_number,
		effective_date: getFormattedDateYmd(plans?.effective_date, true),
		pending_plan_selection: 'Y',
		change_request_id: form_info?.change_id,
		change_request_signature: form_info?.change_id_signature,
	};
}

export function AdminEnrollAllBehalfOfMemberPost(
	dispatch: any,
	values: CoverageFormModel,
	coverageState: NewHireCoverageState,
	employeeId: string,
	employerId: string,
	displayTierMedical: CalculatedTiers,
	displayTierDental: CalculatedTiers,
	qualifyingEvent?: string,
	qualifyingEventDate?: string,
	formID?: number,
	onSuccess?: any,
	queryString?: string,
) {
	const payload: any = {
		client_identifier: employeeId,
		ghl_enrollment_params: {
			global_edited: {
				memo: values?.memo,
				special_requests: values?.specialRequests,
			},
			enrollments_edited: null,
			dependent_enrollments_edited: null,
			form_info: coverageState?.form_info,
		},
	};

	const enrollmentEdited: any = {};
	const dependents = coverageState?.eligible_dependents?.map(
		d => {
			const enrollmentDependent: any = {};
			enrollmentDependent.guid = d?.guid;
			enrollmentDependent.medical = values?.medicalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			enrollmentDependent.dental = values?.dentalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			return enrollmentDependent;
		});



	if (coverageState?.eligible_plans?.medical && values?.M) {
		enrollmentEdited.medical = values?.M ? transformFormData(coverageState?.eligible_plans?.medical, PlanTypes.M, 'M_billingAccountType', values, displayTierMedical?.selected_tier, null, coverageState?.effective_date) : null;
		if (values?.medicalDeclineReason) {
			enrollmentEdited.medical = {
				...enrollmentEdited.medical,
				group_id: coverageState?.eligible_plans?.medical?.group_id,
				decline_reason: values?.medicalDeclineReasonOther ? values?.medicalDeclineReasonOther : values?.medicalDeclineReason,
			};
		}

	}
	if (coverageState?.eligible_plans?.dental) {
		enrollmentEdited.dental = values?.D ? transformFormData(coverageState?.eligible_plans?.dental, PlanTypes.D, 'D_billingAccountType', values, displayTierDental?.selected_tier, null, coverageState?.effective_date) : null;
	}
	if (coverageState?.eligible_plans?.short_term_disability) {
		enrollmentEdited.short_term_disability = values?.W ? transformFormData(coverageState?.eligible_plans?.short_term_disability, PlanTypes.W, 'W_billingAccountType', values, coverageState?.effective_date, coverageState?.effective_date) : null;
	}
	if (coverageState?.eligible_plans?.long_term_disability) {
		enrollmentEdited.long_term_disability = values?.L ? transformFormData(coverageState?.eligible_plans?.long_term_disability, PlanTypes.L, 'L_billingAccountType', values, coverageState?.effective_date, coverageState?.effective_date) : null;
	}
	if (coverageState?.eligible_plans?.group_life) {
		const formVolume = values[`volume${values?.G}`];
		enrollmentEdited.group_life = values?.G ? transformFormData(coverageState?.eligible_plans?.group_life, PlanTypes.G, 'G_billingAccountType', values, null, coverageState?.eligible_plans?.group_life?.plans?.find(p => p?.code === values.G)?.volume ?? formVolume, coverageState?.effective_date) : null;
	}
	if (coverageState?.eligible_plans?.uei) {
		enrollmentEdited.uei = values?.U ? transformFormData(coverageState?.eligible_plans?.uei, PlanTypes.U, 'U_billingAccountType', values, coverageState?.effective_date) : null;
	}

	payload.ghl_enrollment_params.enrollments_edited = enrollmentEdited;
	payload.ghl_enrollment_params.dependent_enrollments_edited = dependents?.filter(d => d?.medical || d?.dental);

	if (qualifyingEvent) {
		payload.ghl_enrollment_params.qualifying_event = qualifyingEvent;
	}

	if (qualifyingEventDate) {
		payload.ghl_enrollment_params.qualifying_event_date = getFormattedDateYmd(qualifyingEventDate, true);
	}

	if (formID) {
		delete payload.ghl_enrollment_params.global_edited;
		delete payload.ghl_enrollment_params.form_info;
		dispatch(postMidYearCoverageEnrollment(employeeId, formID, payload, onSuccess));
		return;
	}
	dispatch(postNewHireCoverageEnrollment(employeeId, queryString, employerId, payload, onSuccess));

}

function DioApproverPost(
	dispatch: any,
	values: CoverageFormModel,
	coverageState: NewHireCoverageState,
	employeeId: string,
	displayTierMedical: CalculatedTiers,
	displayTierDental: CalculatedTiers,
	onSuccess: any,
	currentDioApproverFormId?: number,
) {

	const payload: any = {
		client_identifier: employeeId,
		ghl_form_params: {
			global_edited: {
				memo: values?.memo,
				special_requests: values?.specialRequests,
			},
			enrollments_edited: null,
			dependent_enrollments_edited: null,
			form_info: coverageState?.form_info,
		},
	};
	const enrollmentEdited: any = {};
	const dependents = coverageState?.eligible_dependents?.map(
		d => {
			const enrollmentDependent: any = {};
			enrollmentDependent.guid = d?.guid;
			enrollmentDependent.medical = values?.medicalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			enrollmentDependent.dental = values?.dentalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			return enrollmentDependent;
		});

	if (coverageState?.eligible_plans?.medical) {
		if (coverageState?.eligible_plans?.medical?.member_selection_required) {
			enrollmentEdited.medical = transformFormDataToPushToMember(coverageState?.eligible_plans?.medical, 'M_billingAccountType', values, coverageState?.form_info[coverageState?.eligible_plans?.medical?.group_id]);
		} else if (values?.M) {
			enrollmentEdited.medical = transformFormData(coverageState?.eligible_plans?.medical, PlanTypes.M, 'M_billingAccountType', values, displayTierMedical?.display_tier);
			if (values?.medicalDeclineReason) {
				enrollmentEdited.medical = {
					...enrollmentEdited.medical,
					group_id: coverageState?.eligible_plans?.medical?.group_id,
					decline_reason: values?.medicalDeclineReasonOther ? values?.medicalDeclineReasonOther : values?.medicalDeclineReason,
				};
			}

		}
	}


	if (coverageState?.eligible_plans?.dental) {
		if (coverageState?.eligible_plans?.dental?.member_selection_required) {
			enrollmentEdited.dental = transformFormDataToPushToMember(coverageState?.eligible_plans?.dental, 'D_billingAccountType', values, coverageState?.form_info[coverageState?.eligible_plans?.dental?.group_id]);
		} else if (values?.D) {
			enrollmentEdited.dental = transformFormData(coverageState?.eligible_plans?.dental, PlanTypes.D, 'D_billingAccountType', values, displayTierDental?.display_tier);
		}
	}
	if (coverageState?.eligible_plans?.short_term_disability) {
		if (coverageState?.eligible_plans?.short_term_disability?.member_selection_required) {
			enrollmentEdited.short_term_disability = transformFormDataToPushToMember(coverageState?.eligible_plans?.short_term_disability, 'W_billingAccountType', values, coverageState?.form_info[coverageState?.eligible_plans?.short_term_disability?.group_id]);
		} else if (values?.W) {
			enrollmentEdited.short_term_disability = transformFormData(coverageState?.eligible_plans?.short_term_disability, PlanTypes.W, 'W_billingAccountType', values);
		}
	}
	if (coverageState?.eligible_plans?.long_term_disability) {
		if (coverageState?.eligible_plans?.long_term_disability?.member_selection_required) {
			enrollmentEdited.long_term_disability = transformFormDataToPushToMember(coverageState?.eligible_plans?.long_term_disability, 'L_billingAccountType', values, coverageState?.form_info[coverageState?.eligible_plans?.long_term_disability?.group_id]);
		} else if (values?.L) {
			enrollmentEdited.long_term_disability = transformFormData(coverageState?.eligible_plans?.long_term_disability, PlanTypes.L, 'L_billingAccountType', values);
		}
	}
	if (coverageState?.eligible_plans?.group_life) {
		const formVolume = values[`volume${values?.G}`];
		enrollmentEdited.group_life = values?.G ? transformFormData(coverageState?.eligible_plans?.group_life, PlanTypes.G, 'G_billingAccountType', values, null, coverageState?.eligible_plans?.group_life?.plans?.find(p => p?.code === values.G)?.volume ?? formVolume) : null;
	}
	if (coverageState?.eligible_plans?.uei) {
		enrollmentEdited.uei = values?.U ? transformFormData(coverageState?.eligible_plans?.uei, PlanTypes.U, 'U_billingAccountType', values) : null;
	}

	payload.ghl_form_params.enrollments_edited = enrollmentEdited;
	payload.ghl_form_params.dependent_enrollments_edited = dependents?.filter(d => d?.medical || d?.dental);

	dispatch(postDioApproverApproverGhlForm(currentDioApproverFormId, employeeId, payload, onSuccess));

}

export function transformGhlFormData(
	plans: CoveragePlans,
	planType: PlanTypes,
	billingAccountKey: keyof CoverageFormModel,
	values: CoverageFormModel,
	pushToMember: boolean,
	volume?: string,
	tier?: string,
	decline_reason?: string,
	form_info?: FormInfo,
) {
	const plan = getMatchedPlan(values[planType], plans);

	if (values[planType] === DeclinedValue) {

		let payload: any = {
			declined: YesNoIndicators.Yes,
		};

		if (decline_reason) {
			payload = {
				...payload,
				decline_reason,
			};
		}

		return payload;
	}

	if (!(plans?.plans?.length > 0)) {
		return null;

	}

	if (pushToMember && values[`${planType}_member`]) {
		return transformFormDataToPushToMember(plans, billingAccountKey, values, form_info);
	}

	let payload: any = {
		codes: [plan?.code],
		account_number: values[billingAccountKey] === BillingAccountOptionValues.INSTITUTION ?
			plans?.account_number :
			plans?.diocese_account_number,
		effective_date: getFormattedDateYmd(plans?.effective_date, true),
		pending_plan_selection: 'N',
		declined: null,
	};

	if (tier) {
		payload = {
			...payload,
			tier,
		};
	}
	if (volume) {
		payload = {
			...payload,
			volume: Number(volume) > 0 ? volume : null,
		};
	}
	if (decline_reason) {
		payload = {
			...payload,
			decline_reason,
		};
	}

	if (form_info) {
		payload = {
			...payload,
			change_request_id: form_info?.change_id,
			change_request_signature: form_info?.change_id_signature,
		};
	}

	return payload;

}

export function GetDioApproverInitialValues(coverageState: NewHireCoverageState): DioApproverInitialValues {

	return {

		selectedMedicalPlan: coverageState?.eligible_plans?.medical?.selected?.code ?? DeclinedValue,
		selectedDentalPlan: coverageState?.eligible_plans?.dental?.selected?.code ?? DeclinedValue,
		selectedGroupPlan: coverageState?.eligible_plans?.group_life?.selected?.code ?? DeclinedValue,
		selectedLtdPlan: coverageState?.eligible_plans?.long_term_disability?.selected?.code ?? DeclinedValue,
		selectedStdPlan: coverageState?.eligible_plans?.short_term_disability?.selected?.code ?? DeclinedValue,
		selectedUeiPlan: coverageState?.eligible_plans?.uei?.selected?.code ?? DeclinedValue,
		selectedMedicalBillingAccountNumber: coverageState?.eligible_plans?.medical?.selected?.billing_account_number,
		selectedDentalBillingAccountNumber: coverageState?.eligible_plans?.dental?.selected?.billing_account_number,
		selectedGroupBillingAccountNumber: coverageState?.eligible_plans?.group_life?.selected?.billing_account_number,
		selectedLtdBillingAccountNumber: coverageState?.eligible_plans?.long_term_disability?.selected?.billing_account_number,
		selectedStdBillingAccountNumber: coverageState?.eligible_plans?.short_term_disability?.selected?.billing_account_number,
		selectedUeiBillingAccountNumber: coverageState?.eligible_plans?.uei?.selected?.billing_account_number,
		selectedMedicalTier: coverageState?.eligible_plans?.medical?.selected?.tier,
		selectedDentalTier: coverageState?.eligible_plans?.dental?.selected?.tier,
		medicalDependents: coverageState?.eligible_plans?.medical?.selected?.code ? [SelfText].concat(coverageState?.eligible_plans?.medical?.selected?.dependents) : [],
		dentalDependents: coverageState?.eligible_plans?.dental?.selected?.code ? [SelfText].concat(coverageState?.eligible_plans?.dental?.selected?.dependents) : [],
		medicalDeclineReason: coverageState?.eligible_plans?.medical?.selected?.decline_reason,
		medicalDeclineReasonOther: '', //Decline Other Reason it's not in the api signature given by CPG so it's not implemented yet
		dentalDeclineReason: coverageState?.eligible_plans?.dental?.selected?.decline_reason,
		dentalDeclineReasonOther: '', //Decline Other Reason it's not in the api signature given by CPG so it's not implemented yet
		specialRequests: coverageState?.special_requests,
		volume: coverageState?.eligible_plans?.group_life?.selected?.volume ?? null,
	};
}


function AdminEnrollOrAdminPushToMemberPost(
	dispatch: any,
	values: CoverageFormModel,
	coverageState: NewHireCoverageState,
	employeeId: string,
	employerId: string,
	onSuccess: any,
	whoWillEnrollCriteria: WhoWillEnrollCriterias,
	displayTierMedical: CalculatedTiers,
	displayTierDental: CalculatedTiers,
	qualifyingEvent: string,
	qualifyingEventDate: string,
	billingAccountCn = null,
) {
	const ghl_form_params: any = {
		employer_client_number: billingAccountCn ? null : employerId,
		qualifying_event: qualifyingEvent,
		qualifying_event_date: billingAccountCn ? null : getFormattedDateYmd(qualifyingEventDate, true),
		extenuating_circumstances: null,
		memo: values?.memo,
		special_requests: values?.specialRequests,
		enrollments_edited: null,
		dependent_enrollments_edited: null,
	};

	const enrollmentEdited: any = {};
	const dependents = coverageState?.eligible_dependents?.map(
		d => {
			const enrollmentDependent: any = {};
			enrollmentDependent.guid = d?.guid;
			enrollmentDependent.medical = values?.medicalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			enrollmentDependent.dental = values?.dentalDependents?.includes(d?.guid) ? YesNoIndicators.Yes : YesNoIndicators.No;
			return enrollmentDependent;
		});

	if (coverageState?.eligible_plans?.medical) {
		enrollmentEdited.medical = transformGhlFormData(
			coverageState?.eligible_plans?.medical,
			PlanTypes.M,
			'M_billingAccountType',
			values,
			whoWillEnrollCriteria?.medical === WhoWillEnrollOptions.member,
			null,
			displayTierMedical?.selected_tier,
			values?.medicalDeclineReason === declineCoverageReasonOther ? values?.medicalDeclineReasonOther : values?.medicalDeclineReason,
			coverageState?.form_info[coverageState?.eligible_plans?.medical?.group_id],
		);
	}
	if (coverageState?.eligible_plans?.dental) {
		enrollmentEdited.dental = transformGhlFormData(
			coverageState?.eligible_plans?.dental,
			PlanTypes.D,
			'D_billingAccountType',
			values,
			whoWillEnrollCriteria?.dental === WhoWillEnrollOptions.member,
			null,
			displayTierDental?.selected_tier,
			null,
			coverageState?.form_info[coverageState?.eligible_plans?.dental?.group_id],
		);
	}
	if (coverageState?.eligible_plans?.short_term_disability) {
		enrollmentEdited.short_term_disability = transformGhlFormData(
			coverageState?.eligible_plans?.short_term_disability,
			PlanTypes.W,
			'W_billingAccountType',
			values,
			whoWillEnrollCriteria?.std === WhoWillEnrollOptions.member,
			null,
			null,
			null,
			coverageState?.form_info[coverageState?.eligible_plans?.short_term_disability?.group_id],
		);
	}
	if (coverageState?.eligible_plans?.long_term_disability) {
		enrollmentEdited.long_term_disability = transformGhlFormData(
			coverageState?.eligible_plans?.long_term_disability,
			PlanTypes.L,
			'L_billingAccountType',
			values,
			whoWillEnrollCriteria?.ltd === WhoWillEnrollOptions.member,
			null,
			null,
			null,
			coverageState?.form_info[coverageState?.eligible_plans?.long_term_disability?.group_id],
		);
	}
	if (coverageState?.eligible_plans?.group_life) {
		const formVolume = values[`volume${values?.G}`];
		enrollmentEdited.group_life = transformGhlFormData(
			coverageState?.eligible_plans?.group_life,
			PlanTypes.G,
			'G_billingAccountType',
			values,
			false,
			coverageState?.eligible_plans?.group_life?.plans?.find(p => p?.code === values.G)?.volume ?? formVolume,
			null,
			null,
			coverageState?.form_info[coverageState?.eligible_plans?.group_life?.group_id],
		);
	}
	if (coverageState?.eligible_plans?.uei) {
		enrollmentEdited.uei = transformGhlFormData(
			coverageState?.eligible_plans?.uei,
			PlanTypes.U,
			'U_billingAccountType',
			values,
			false,
			null,
			null,
			null,
			coverageState?.form_info[coverageState?.eligible_plans?.uei?.group_id],
		);
	}

	ghl_form_params.enrollments_edited = enrollmentEdited;
	ghl_form_params.dependent_enrollments_edited = dependents?.filter(d => d?.medical || d?.dental);
	const payload: any = {
		ghl_form_params,
	};
	if (billingAccountCn) {
		payload.account_number = employerId;
	}
	dispatch(postGhlForm(employeeId, payload, onSuccess));

}

export function onSubmit(
	dispatch: any,
	step: BenefitEnrollmentStep,
	values: CoverageFormModel,
	coverageState: NewHireCoverageState,
	employeeId: string,
	employerId: string,
	whoWillEnrollCriteria: WhoWillEnrollCriterias,
	onSuccess: any,
	displayTierMedical: CalculatedTiers,
	displayTierDental: CalculatedTiers,
	qualifyingEvent: string,
	qualifyingEventDate: string,
	queryString: string,
	billingAccountCn = null,
	institutionOfficerOnly = false,
	isDioApprover?: boolean,
	currentDioApproverFormId?: number,
) {
	if (step === BenefitEnrollmentStep.COVERAGE) {
		dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.REVIEW));
		window.scroll({ top: 0, left: 0, behavior: 'smooth' });
		return;
	}

	if (
		whoWillEnrollCriteria?.medical === WhoWillEnrollOptions.member ||
		whoWillEnrollCriteria?.dental === WhoWillEnrollOptions.member ||
		whoWillEnrollCriteria?.std === WhoWillEnrollOptions.member ||
		whoWillEnrollCriteria?.ltd === WhoWillEnrollOptions.member ||
		institutionOfficerOnly
	) {
		AdminEnrollOrAdminPushToMemberPost(
			dispatch,
			values,
			coverageState,
			employeeId,
			employerId,
			(res) => onSuccess(res),
			whoWillEnrollCriteria,
			displayTierMedical,
			displayTierDental,
			qualifyingEvent,
			qualifyingEventDate,
			billingAccountCn,
		);
		return;
	}

	if (isDioApprover) {
		DioApproverPost(dispatch, values, coverageState, employeeId, displayTierMedical, displayTierDental, (res) => onSuccess(res), currentDioApproverFormId);
		return;
	}

	AdminEnrollAllBehalfOfMemberPost(
		dispatch,
		values,
		coverageState,
		employeeId,
		employerId,
		displayTierMedical,
		displayTierDental,
		qualifyingEvent,
		qualifyingEventDate,
		currentDioApproverFormId,
		(res) => onSuccess(res),
		queryString,
	);
	//post
}



export function getBillingAccountInitialValues(billingPref: BillingPrefernce) {
	if (!billingPref) {
		return BillingAccountOptionValues.INSTITUTION;
	}

	switch (billingPref) {
		case BillingPrefernce.ALLOWED:
			return BillingAccountOptionValues.INSTITUTION;

		case BillingPrefernce.DISALLOWED:
			return BillingAccountOptionValues.INSTITUTION;
		case BillingPrefernce.REQUIRED:
			return BillingAccountOptionValues.DIOCESE;

		default:
			return BillingAccountOptionValues.INSTITUTION;

	}

}

export function GetCoveragePlanBillingAccountInitialValues(coveragePlan: CoveragePlans) {
	if (coveragePlan?.current) {
		if (coveragePlan?.diocese_account_number === coveragePlan?.current?.account_number) {
			return BillingAccountOptionValues.DIOCESE;
		}

		return BillingAccountOptionValues.INSTITUTION;
	}

	const billingPref = coveragePlan?.billing_preference;
	if (!billingPref) {
		return BillingAccountOptionValues.INSTITUTION;
	}

	switch (billingPref) {
		case BillingPrefernce.ALLOWED:
			return BillingAccountOptionValues.INSTITUTION;

		case BillingPrefernce.DISALLOWED:
			return BillingAccountOptionValues.INSTITUTION;
		case BillingPrefernce.REQUIRED:
			return BillingAccountOptionValues.DIOCESE;

		default:
			return BillingAccountOptionValues.INSTITUTION;

	}

}

export function getBillingAccountInitialValuesForDioAdmin(accountNumber: string, plan: CoveragePlans) {
	if (plan?.billing_preference === BillingPrefernce.ALLOWED && accountNumber) {
        return BillingAccountOptionValues.INSTITUTION;
    }

	if (plan?.billing_preference === BillingPrefernce.DISALLOWED) {
		return BillingAccountOptionValues.INSTITUTION;
	}

	if (plan?.billing_preference) {
		return BillingAccountOptionValues.DIOCESE;
	}

	return BillingAccountOptionValues.INSTITUTION;
}

export function GetCoveragePlanInitialValue(coveragePlan: CoveragePlans): string {

	return coveragePlan?.current?.code ?? null;
}

export function GetCoveragePlanDependentsInitialValue(coveragePlan: CoveragePlans): string[] {
	return coveragePlan?.current?.code ? [SelfText].concat(coveragePlan?.current?.dependents) : [];
}

export function getBillingAccountDisabledState(billingPref: BillingPrefernce) {
	if (!billingPref) {
		return true;
	}

	switch (billingPref) {
		case BillingPrefernce.ALLOWED:
			return false;

		case BillingPrefernce.DISALLOWED:
			return true;
		case BillingPrefernce.REQUIRED:
			return true;

		default:
			return false;

	}

}

export function GetDefaultPlantoEnroll(plans: CoveragePlans, memberEnroll?: boolean) {
	return !memberEnroll ? plans?.plans[0]?.code : '';
}

export function getRateRecordUsingVolume(groupId: string, employment_id: number | unknown, value: number, groupLifeField: string, dispatch: any) {

	dispatch(getVolumeForSpecificPlans(groupId, employment_id, value, (response: any) => {
		dispatch(setVolumeAndRate(value, response?.data?.group_life_rate, groupLifeField));
	}));
}

export function GetNewHireConfirmationRedirectModel(response) {

	return {
		apiWarning: {
			message: response?.status?.message,
			warnings:
				[].
					concat(response?.status?.errors?.general)?.
					concat(response?.status?.errors?.effective_dates)?.
					concat(response?.status?.errors?.dependent_enrollments)?.
					concat(response?.status?.errors?.late_enrollments)?.
					filter(err => !!err),
		},
	};
}

export function ShouldShowAdminCafeteriaAcknowledgement(plans: NewHireCoverageState, formValue: CoverageFormModel, numberOfDays: number) {

	const hasAvailable = plans?.eligible_plans?.medical?.plans || plans?.eligible_plans?.dental?.plans || plans?.eligible_plans?.long_term_disability?.plans || plans?.eligible_plans?.short_term_disability?.plans;
	if (!hasAvailable) return false;

	let completeEnrollment: boolean = true;

	if (plans?.eligible_plans?.medical?.plans?.length > 0) {

		if (formValue?.M && completeEnrollment) {
			completeEnrollment = true;
		}
		if (!formValue?.M || !completeEnrollment) {
			completeEnrollment = false;
		}

	}

	if (plans?.eligible_plans?.dental?.plans?.length > 0) {

		if (formValue?.D && completeEnrollment) {
			completeEnrollment = true;
		}
		if (!formValue?.D || !completeEnrollment) {
			completeEnrollment = false;
		}

	}

	if (plans?.eligible_plans?.short_term_disability?.plans?.length > 0) {

		if (formValue?.W && completeEnrollment) {
			completeEnrollment = true;
		}
		if (!formValue?.W || !completeEnrollment) {
			completeEnrollment = false;
		}

	}

	if (plans?.eligible_plans?.long_term_disability?.plans?.length > 0) {

		if (formValue?.L && completeEnrollment) {
			completeEnrollment = true;
		}
		if (!formValue?.L || !completeEnrollment) {
			completeEnrollment = false;
		}

	}

	const enrolledInPending = formValue?.M || formValue?.D || formValue?.L || formValue?.W;
	const amountOfDays = completeEnrollment ? 30 : 16;

	return !!(completeEnrollment || enrolledInPending) && (numberOfDays < (-amountOfDays) && numberOfDays <= 0);
}

export function GetAdminValidationModel(coverageState: NewHireCoverageState): CoverageValidationSchemaModel {
	return {

		hasMedical: coverageState?.eligible_plans?.medical?.plans?.length > 0,
		hasDental: coverageState?.eligible_plans?.dental?.plans?.length > 0,
		hasGroup: coverageState?.eligible_plans?.group_life?.plans?.length > 0,
		hasLtd: coverageState?.eligible_plans?.long_term_disability?.plans?.length > 0,
		hasStd: coverageState?.eligible_plans?.short_term_disability?.plans?.length > 0,
		hasUei: coverageState?.eligible_plans?.uei?.plans?.length > 0,
	};
}

export function GetMemberSelectionRequiredValidationModel(coverageState: NewHireCoverageState, whoWillEnroll: WhoWillEnrollCriterias, adminValidationModel: CoverageValidationSchemaModel): MemberSelectionRequiredValidationSchemaModel {
	return {
		memberWillSelectMedical: adminValidationModel?.hasMedical && (whoWillEnroll?.medical === WhoWillEnrollOptions.member || coverageState?.eligible_plans?.medical?.member_selection_required),
		memberWillSelectDental: adminValidationModel?.hasDental && (whoWillEnroll?.dental === WhoWillEnrollOptions.member || coverageState?.eligible_plans?.dental?.member_selection_required),
		memberWillSelectLtd: adminValidationModel?.hasLtd && (whoWillEnroll?.ltd === WhoWillEnrollOptions.member || coverageState?.eligible_plans?.long_term_disability?.member_selection_required),
		memberWillSelectStd: adminValidationModel?.hasStd && (whoWillEnroll?.std === WhoWillEnrollOptions.member || coverageState?.eligible_plans?.short_term_disability?.member_selection_required),
	};
}

export const validatePlanContentDioApprover = (plan: CoveragePlans, isDioApprover: boolean): boolean => {

	if (!plan?.member_selection_required) {
		return true;
	}

	if (isDioApprover && plan?.member_selection_required) {
		return false;
	}

	return true;
};

export const itsCoverageFormUpdate = (basePath: string) => {
	return basePath.includes(ArcRoutes.UpdateEnrollment);
};